import type {
	CreateZoneRequest,
	UpdateZoneRequest,
	Zone,
} from "@sitescene/types";

import { apiClient } from "../client";
import { endpoints } from "../endpoints";

/**
 * Zones API Service
 *
 * Pure functions for zone-related API calls.
 * No React dependencies - can be used anywhere.
 *
 * This is the API layer - it only handles HTTP communication.
 * Business logic and state management happen in React Query hooks.
 */

export const zonesApi = {
	/**
	 * Create a new zone
	 */
	createZone: async (zone: CreateZoneRequest): Promise<Zone> => {
		const response = await apiClient.post<Zone>(endpoints.zones.create, zone);
		return response.data;
	},

	/**
	 * Delete a zone
	 *
	 * TODO: Add a real DELETE zone API (e.g. DELETE /zones/{zoneId}), register it
	 * in endpoints.ts, and implement ZoneService.delete + Lambda. Then expose
	 * delete in the location zones UI via useDeleteZone.
	 */
	deleteZone: async (locationId: string, zoneId: string): Promise<void> => {
		// Note: Update endpoints.ts if there's a delete endpoint
		await apiClient.delete<void>(`/locations/${locationId}/zones/${zoneId}`);
	},

	/**
	 * Get a single zone by ID (via location endpoint)
	 */
	getZone: async (locationId: string, zoneId: string): Promise<Zone> => {
		// Note: If there's a direct zone endpoint, use it. Otherwise, fetch all and filter.
		const zones = await zonesApi.getZones(locationId);
		const zone = zones.find((z) => z.id === zoneId);
		if (!zone) {
			throw new Error(`Zone ${zoneId} not found`);
		}
		return zone;
	},

	/**
	 * Get all zones for a location
	 */
	getZones: async (locationId: string): Promise<Zone[]> => {
		const response = await apiClient.get<Zone[]>(
			endpoints.zones.get(locationId),
		);
		return response.data;
	},

	/**
	 * Update an existing zone
	 */
	updateZone: async (
		zoneId: string,
		updates: UpdateZoneRequest,
	): Promise<Zone> => {
		const response = await apiClient.patch<Zone>(
			endpoints.zones.update(zoneId),
			updates,
		);
		return response.data;
	},
};
